/**
 * localStorage-backed panel size hook.
 *
 * Layout panels (log, sidebar) are user-resizable. We store the chosen
 * size under the `mcptc:ui.` namespace so the app remembers across reloads
 * and the other layout state persistence already scoped to that prefix.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { appStore } from '../state/store-instance.ts';
import { uiKey } from '../persistence/schema.ts';

/**
 * Read/write a `react-resizable-panels` `Layout` (panel-id → flexGrow map)
 * from `mcptc:ui.layout.<feature>`. Used by the AppShell-level resizable
 * splits so the user's chosen sizes survive reloads.
 *
 * Returns a `[layout, onLayoutChanged]` tuple suitable for direct binding
 * to a `<Group defaultLayout={layout} onLayoutChanged={onLayoutChanged}>`.
 */
export function usePersistedLayout(
  feature: string,
): [Layout | undefined, (layout: Layout) => void] {
  const storageKey = uiKey(`layout.${feature}`);
  // Read once on mount; the Layout map is what RRP expects.
  const initial = useRef<Layout | undefined>(undefined);
  if (initial.current === undefined) {
    const stored = appStore.read<Layout>(storageKey);
    initial.current =
      stored && typeof stored === 'object' && !Array.isArray(stored) ? stored : undefined;
  }

  const onLayoutChanged = useCallback(
    (layout: Layout) => {
      appStore.write(storageKey, layout);
    },
    [storageKey],
  );

  return [initial.current, onLayoutChanged];
}

/** Map of panel id → flex-grow value, matching `react-resizable-panels`. */
export type Layout = Record<string, number>;

export interface PanelSizeBounds {
  min: number;
  max: number;
  default: number;
}

/**
 * Returns `[size, setSize]` where `size` is guaranteed to fall within
 * `bounds.min..bounds.max`. Persisted to localStorage under
 * `mcptc:ui.<feature>`.
 */
export function usePanelSize(
  feature: string,
  bounds: PanelSizeBounds,
): [number, (value: number) => void] {
  const [size, setLocalSize] = useState<number>(() => {
    const stored = appStore.read<number>(uiKey(feature));
    const seed = typeof stored === 'number' && Number.isFinite(stored) ? stored : bounds.default;
    return clamp(seed, bounds.min, bounds.max);
  });

  // Re-clamp if bounds tighten (e.g., viewport got smaller).
  useEffect(() => {
    setLocalSize((current) => clamp(current, bounds.min, bounds.max));
  }, [bounds.min, bounds.max]);

  const setSize = useCallback(
    (value: number) => {
      const clamped = clamp(value, bounds.min, bounds.max);
      setLocalSize(clamped);
      appStore.write(uiKey(feature), clamped);
    },
    [feature, bounds.min, bounds.max],
  );

  return [size, setSize];
}

export function clamp(value: number, min: number, max: number): number {
  if (value < min) return min;
  if (value > max) return max;
  return value;
}
